import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useIsMobile } from "../../hooks/use-mobile";
import { cn } from "../../lib/utils";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";

type DataTablePaginationProps = {
  onChange: (page: number, perPage: number) => void;
  current: number;
  total: number;
  showPageSizeOptions?: boolean;
  pageSizeOptions: number[];
  perPage: number;
  /** Construit l'URL réelle d'une page, pour un lien crawlable par les moteurs de recherche. */
  getPageHref?: (page: number) => string;
};

const mobileNavLinkClass =
  "flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-black";

const MobileNavLink = ({
  enabled,
  href,
  onClick,
  children,
}: {
  enabled: boolean;
  href: string;
  onClick: () => void;
  children: React.ReactNode;
}) => {
  if (!enabled) {
    return (
      <span className={cn(mobileNavLinkClass, "opacity-50 cursor-not-allowed")}>
        {children}
      </span>
    );
  }

  return (
    <a
      href={href}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={mobileNavLinkClass}
    >
      {children}
    </a>
  );
};

const DataTablePagination = ({
  onChange,
  current,
  total,
  pageSizeOptions,
  perPage,
  showPageSizeOptions = true,
  getPageHref = () => "#",
}: DataTablePaginationProps) => {
  const maxVisiblePages = 10;
  const isMobile = useIsMobile();

  // Calculer les pages à afficher
  const getVisiblePages = () => {
    if (total <= maxVisiblePages) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    const pages: (number | "ellipsis")[] = [];

    // Toujours afficher la première page
    pages.push(1);

    if (current <= 4) {
      // Si on est proche du début, afficher les premières pages
      for (let i = 2; i <= Math.min(6, total - 1); i++) {
        pages.push(i);
      }
      if (total > 7) {
        pages.push("ellipsis");
      }
    } else if (current >= total - 3) {
      // Si on est proche de la fin, afficher les dernières pages
      if (total > 7) {
        pages.push("ellipsis");
      }
      for (let i = Math.max(total - 5, 2); i <= total - 1; i++) {
        pages.push(i);
      }
    } else {
      // Si on est au milieu, afficher autour de la page courante
      pages.push("ellipsis");
      for (
        let i = Math.max(current - 2, 2);
        i <= Math.min(current + 2, total - 1);
        i++
      ) {
        pages.push(i);
      }
      pages.push("ellipsis");
    }

    // Toujours afficher la dernière page
    if (total > 1) {
      pages.push(total);
    }

    return pages;
  };

  const visiblePages = getVisiblePages();
  const hasPrevious = current > 1;
  const hasNext = current < total;

  const handlePageChange = (page: number) => {
    onChange(page, perPage);
  };

  const handlePerPageChange = (newPerPage: string) => {
    onChange(1, parseInt(newPerPage)); // Reset à la page 1 quand on change le perPage
  };

  if (isMobile) {
    return (
      <div className="flex flex-1 justify-between px-4 sm:hidden">
        <MobileNavLink
          enabled={hasPrevious}
          href={getPageHref(current - 1)}
          onClick={() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
            handlePageChange(current - 1);
          }}
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Précédent
        </MobileNavLink>
        <div className="flex items-center gap-2">
          <SelectPerPage
            pageSizeOptions={pageSizeOptions}
            perPage={perPage}
            onChange={handlePerPageChange}
          />
          <MobileNavLink
            enabled={hasNext}
            href={getPageHref(current + 1)}
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              handlePageChange(current + 1);
            }}
          >
            Suivant
            <ArrowRightIcon className="w-4 h-4" />
          </MobileNavLink>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-end gap-2 px-2 py-4">
      <Pagination>
        <PaginationContent>
          {hasPrevious && (
            <PaginationItem>
              <PaginationPrevious
                href={getPageHref(current - 1)}
                size="default"
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  handlePageChange(current - 1);
                }}
              />
            </PaginationItem>
          )}

          {visiblePages.map((page, index) => (
            <PaginationItem key={index}>
              {page === "ellipsis" ? (
                <PaginationEllipsis />
              ) : (
                <PaginationLink
                  href={getPageHref(page)}
                  isActive={page === current}
                  size="icon"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePageChange(page);
                  }}
                >
                  {page}
                </PaginationLink>
              )}
            </PaginationItem>
          ))}

          {hasNext && (
            <PaginationItem>
              <PaginationNext
                href={getPageHref(current + 1)}
                size="default"
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  handlePageChange(current + 1);
                }}
              />
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
      {showPageSizeOptions && (
        <SelectPerPage
          pageSizeOptions={pageSizeOptions}
          perPage={perPage}
          onChange={handlePerPageChange}
        />
      )}
    </div>
  );
};

export default DataTablePagination;

const SelectPerPage = ({
  pageSizeOptions,
  perPage,
  onChange,
}: {
  pageSizeOptions: number[];
  perPage: number;
  onChange: (newPerPage: string) => void;
}) => {
  return (
    <Select value={perPage.toString()} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-[75px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent side="top">
        {pageSizeOptions.map((size: number) => (
          <SelectItem key={size} value={size.toString()}>
            {size}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
